import React, { useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { Link, useLocation } from 'react-router-dom';
import { setBusinessRecords } from '../../data-layer/business/actions';
import * as businessService from '../../data-layer/business/service';
import { OfferAgreement } from '../../data-layer/business/types';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { Dialog, DialogContent } from '../../ui/feedback/Dialog';
import { Button } from '../../ui/input/Button';
import { Checkbox } from '../../ui/input/Checkbox';
import { FormControlLabel } from '../../ui/input/FormControlLabel';
import { Flex } from '../../ui/layout/Flex';
import { Typography } from '../../ui/typography/Typography';
import { ymTrackOfferAgreement } from '../../utils/metrika';
import { useBackButton } from '../../utils/useBackButton';
import { useTranslation } from '../../utils/useTranslation';
import { composeValidators } from '../../utils/validators/compose';
import { makeRequiredValidator } from '../../utils/validators/makeRequiredValidator';
import { useCurrentUser } from '../users/useCurrentUser';

const validateOfferAgreement = composeValidators(makeRequiredValidator<string>());

interface FormValues {
    offerAgreement: boolean;
}

export function OfferAgreementDialog() {
    const dispatch = useDispatch();
    const t = useTranslation();
    const onNavigateBack = useBackButton();
    const currentUser = useCurrentUser();
    const { state } = useLocation();
    const { businessId } = state;
    const onSubmit = useCallback(
        async (formValues: FormValues) => {
            try {
                const response = await businessService.updateBusiness(businessId, {
                    offerAgreement: formValues.offerAgreement
                        ? OfferAgreement.SIGNED
                        : OfferAgreement.UNSET,
                });

                dispatch(setBusinessRecords(...response.records.business));
                ymTrackOfferAgreement({
                    page: 'Попап оферты',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });
                onNavigateBack();
            } catch (error) {
                dispatch(
                    showNotificationAsync(
                        'Не удалось подписать оферту, обновите страницу и попробуйте еще раз',
                    ),
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, businessId],
    );

    return (
        <Dialog height="auto" borderRadius={3} maxWidth={{ xs: 'calc(100% - 32px)', sm: 580 }}>
            <DialogContent py={2} px={2}>
                <Form
                    onSubmit={onSubmit}
                    initialValues={{
                        offerAgreement: false,
                    }}
                    render={({ submitting, pristine, invalid, submitError, handleSubmit }) => (
                        <Flex
                            as="form"
                            width="100%"
                            height={{ xs: '100%', sm: 'auto' }}
                            onSubmit={handleSubmit}
                        >
                            <Typography>
                                {t(
                                    'Для использования функциональности сервиса Яндекс Ритм примите юридические условия',
                                )}
                            </Typography>
                            <Field
                                type="checkbox"
                                name="offerAgreement"
                                validate={validateOfferAgreement}
                                render={({ input }) => (
                                    <FormControlLabel
                                        label={
                                            <Typography>
                                                {t('Принимаю ')}
                                                <Typography
                                                    display="inline"
                                                    as={Link}
                                                    to={process.env.REACT_APP_TERM_OF_USE || ''}
                                                    color="link"
                                                    textDecoration="none"
                                                    target="_blank"
                                                >
                                                    {t('пользовательское соглашение')}
                                                </Typography>
                                            </Typography>
                                        }
                                        control={<Checkbox {...input} />}
                                    />
                                )}
                            />
                            {submitError ? (
                                <Typography variant="caption" color="error" mb={2}>
                                    {submitError}
                                    <br />
                                </Typography>
                            ) : null}
                            <Button
                                width="100%"
                                type="submit"
                                size="xl"
                                disabled={pristine || submitting || invalid || !businessId}
                            >
                                {t('Принимаю')}
                            </Button>
                        </Flex>
                    )}
                />
            </DialogContent>
        </Dialog>
    );
}
